const defaultPaymentMilestoneCreates = new Set<string>()

export function getDefaultPaymentMilestoneId(quoteId: string, sequenceNumber: number) {
  const hex = quoteId.replace(/-/g, '').toLowerCase()
  if (!/^[\da-f]{32}$/.test(hex)) {
    throw new Error('Quote ID is not a valid GUID')
  }

  const tail = (Number.parseInt(hex.slice(28), 16) ^ sequenceNumber).toString(16).padStart(4, '0')
  const id = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 28)}${tail}`
  return id
}

export function getAvailableDefaultPaymentMilestoneId(
  quoteId: string,
  sequenceNumber: number,
  occupiedIds: ReadonlySet<string>
) {
  if (!Number.isInteger(sequenceNumber) || sequenceNumber < 1 || sequenceNumber > 255) {
    throw new Error('Default payment milestone sequence must be between 1 and 255')
  }

  for (let attempt = 0; attempt < 256; attempt++) {
    const candidate = getDefaultPaymentMilestoneId(quoteId, sequenceNumber + attempt * 256)
    if (!occupiedIds.has(candidate)) return candidate
  }

  throw new Error('No deterministic payment milestone ID is available')
}

/**
 * Default ids for `sequenceNumbers`, skipping ids a milestone outside this quote
 * holds. A quote id can come back: a change order deleted and re-created by the
 * same request gets its old id, and deleting a quote only unlinks its
 * milestones (RemoveLink). Those orphans keep the ids derived from it, and an
 * upsert onto one 412s forever. Ids this quote's own milestones hold are not
 * skipped: colliding with them is the dedupe against a concurrent create.
 */
export async function resolveDefaultPaymentMilestoneIds(
  quoteId: string,
  sequenceNumbers: number[],
  occupiedIds: ReadonlySet<string>,
  findHolders: (ids: string[]) => Promise<{ id: string; quoteId: string | null }[]>
) {
  if (sequenceNumbers.length === 0) return []
  const occupied = new Set(occupiedIds)
  for (;;) {
    const picked = new Set(occupied)
    const ids = sequenceNumbers.map((sequenceNumber) => {
      const id = getAvailableDefaultPaymentMilestoneId(quoteId, sequenceNumber, picked)
      picked.add(id)
      return id
    })
    const heldElsewhere = (await findHolders(ids)).filter((holder) => holder.quoteId !== quoteId.toLowerCase())
    if (heldElsewhere.length === 0) return ids
    for (const holder of heldElsewhere) occupied.add(holder.id)
  }
}

export function claimDefaultPaymentMilestoneCreate(quoteId: string) {
  if (defaultPaymentMilestoneCreates.has(quoteId)) return false
  defaultPaymentMilestoneCreates.add(quoteId)
  return true
}

export function releaseDefaultPaymentMilestoneCreate(quoteId: string) {
  defaultPaymentMilestoneCreates.delete(quoteId)
}

// The claim is held while anything writes a quote's milestones (the dialog's
// save, or the plugin's default create); the plugin's re-projection/delete-all
// must not act on the half-written state.
export function isPaymentMilestoneWriteInFlight(quoteId: string | undefined) {
  return !!quoteId && defaultPaymentMilestoneCreates.has(quoteId)
}
